import { createSelector } from "reselect";
import { CommerceToolsApi } from "@grandvisionhq/commercetools";
import { Util } from "@grandvisionhq/forms";
import { Types } from "@grandvisionhq/graphql";
import { isDefined } from "@grandvisionhq/utils";
import { isNewPrescriptionAndUpload } from "../../../../utils/utils/is-new-prescription-upload.js";
import { getIsPupillaryDistanceFileOptional } from "../../../configurations/state/configurations/selectors.js";
import { getConfiguration } from "../../../configurations/state/configurations/selectors.internal.js";
import { getAdditionalPrescriptionInfoFormState, getFrameSku } from "../../../configurator/state/configurator/selectors.js";
import { isBookAppointmentEntrySelection } from "../../../state/types.js";
const getAdditionalPrescriptionInfo = createSelector(
  getAdditionalPrescriptionInfoFormState,
  (additionalPrescriptionInfoFormState) => {
    if (!additionalPrescriptionInfoFormState) {
      return {};
    }
    const { expiryDate, ...rest } = Util.getValuesFromState(
      additionalPrescriptionInfoFormState
    );
    return {
      ...expiryDate ? { expiryDate } : {},
      opticianDetails: {
        ...rest
      }
    };
  }
);
const configureBundleLineItemsForCart = (config) => {
  if (!config) {
    return;
  }
  const bundleLineItems = [];
  Object.keys(config).forEach((item) => {
    let sku;
    if (item === "prescriptionType" || !config[item]) {
      return;
    }
    if (item === "lensType" && config?.lensType?.sku) {
      sku = config.lensType.sku;
    }
    bundleLineItems.push({
      sku: sku ?? config[item]
    });
  });
  return bundleLineItems;
};
const configurePrescriptionInfo = (config, additionalInfo, isPrescriptionRecognitionEnabled, isPDFileOptional) => {
  const prescriptionType = config?.prescriptionType;
  if (!prescriptionType || isBookAppointmentEntrySelection(prescriptionType)) {
    return;
  }
  if (CommerceToolsApi.isEmailPrescription(prescriptionType)) {
    return { type: "email-prescription" };
  }
  if (CommerceToolsApi.isExistingCustomer(prescriptionType)) {
    return { type: "existing-customer" };
  }
  const prescriptionInfo = config?.lensType?.prescriptionInfo;
  if (CommerceToolsApi.isNewPrescription(prescriptionType)) {
    if (!prescriptionInfo) {
      return;
    }
    const { right, left } = prescriptionInfo;
    return {
      type: "new-prescription",
      details: {
        rightLens: right,
        leftLens: left,
        source: Types.PrescriptionSource.MANUAL,
        // by default the source is 'MANUAL', or it may become 'OCR' if the prescription is uploaded and recognized
        ...additionalInfo
      }
    };
  }
  if (CommerceToolsApi.isPrescriptionFile(prescriptionType) || isNewPrescriptionAndUpload(prescriptionType)) {
    const uploadReferences = prescriptionInfo?.uploadReferences;
    const hasPupillaryDistancePrescriptionInfo = isDefined(prescriptionInfo?.left.pupillaryDistance) && isDefined(prescriptionInfo?.right?.pupillaryDistance);
    const isPDInputValid = uploadReferences?.["pg-pupillary-distance"] || isPDFileOptional && hasPupillaryDistancePrescriptionInfo;
    if (!uploadReferences || !uploadReferences["pg-prescription"] || !isPDInputValid) {
      return;
    }
    const prescriptionSource = !isNewPrescriptionAndUpload(prescriptionType) && isPrescriptionRecognitionEnabled ? Types.PrescriptionSource.OCR : Types.PrescriptionSource.MANUAL;
    if (prescriptionInfo && (isPrescriptionRecognitionEnabled || isNewPrescriptionAndUpload(prescriptionType))) {
      const { right, left } = prescriptionInfo;
      return {
        type: "prescription-file",
        details: {
          prescriptionFile: uploadReferences["pg-prescription"],
          pupillaryDistanceFile: uploadReferences["pg-pupillary-distance"],
          left,
          right,
          source: prescriptionSource
        }
      };
    }
    return {
      type: "prescription-file",
      details: {
        prescriptionFile: uploadReferences["pg-prescription"],
        pupillaryDistanceFile: uploadReferences["pg-pupillary-distance"],
        source: prescriptionSource
      }
    };
  }
};
const getAddToCartLineItem = (isPrescriptionRecognitionEnabled) => createSelector(
  getFrameSku,
  (s) => s,
  getIsPupillaryDistanceFileOptional,
  (frameSku, state, isPDFileOptional) => {
    if (!frameSku) {
      return void 0;
    }
    const lineItemRawConfig = getConfiguration(state, { frameSku });
    const additionalPrescriptionInfo = getAdditionalPrescriptionInfo(state);
    const bundleLineItems = configureBundleLineItemsForCart(lineItemRawConfig);
    const prescription = configurePrescriptionInfo(
      lineItemRawConfig,
      additionalPrescriptionInfo,
      isPrescriptionRecognitionEnabled,
      isPDFileOptional
    );
    if (!bundleLineItems || !prescription) {
      return void 0;
    }
    return {
      type: "prescription-glasses",
      sku: frameSku,
      bundleLineItems,
      prescription
    };
  }
);
export {
  getAddToCartLineItem,
  getAdditionalPrescriptionInfo
};
//# sourceMappingURL=selectors.js.map
